import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api'
import type { PaginatedResponse } from '@/types'

const FINANCE_KEY = 'finance'

export interface Transaction {
  id: string
  tenantId: string
  branchId?: string | null
  branchName?: string | null
  type: 'Income' | 'Expense' | 'income' | 'expense'
  category: string
  description: string
  amount: number
  currency?: string
  transactionDate: string
  date?: string
  createdAt: string
}

export interface MonthlyBreakdown {
  year: number
  month: number
  monthName: string
  income: number
  expense: number
  net: number
}

export interface FinanceSummary {
  totalIncome: number
  totalExpense: number
  net: number
  netBalance?: number
  period?: string
  monthlyBreakdown: MonthlyBreakdown[]
  monthlyData?: Array<{ month: string; gelir: number; gider: number }>
}

export interface TransactionFilters {
  type?: 'income' | 'expense'
  category?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  pageSize?: number
}

export interface CreateTransactionPayload {
  type: 'income' | 'expense'
  category: string
  description: string
  amount: number
  currency?: string
  date: string
  branchId?: string
}

export interface UpdateTransactionPayload {
  type: 'income' | 'expense'
  category: string
  description: string
  amount: number
  date: string
}

export const useTransactions = (params?: TransactionFilters) =>
  useQuery({
    queryKey: [FINANCE_KEY, 'transactions', params],
    queryFn: () =>
      apiGet<PaginatedResponse<Transaction>>('/finance/transactions', { params }),
  })

export const useFinanceSummary = (period?: string) =>
  useQuery({
    queryKey: [FINANCE_KEY, 'summary', period],
    queryFn: () =>
      apiGet<FinanceSummary>('/finance/summary', {
        params: period ? { period } : undefined,
      }),
  })

const TYPE_MAP: Record<string, string> = {
  income: 'Income',
  expense: 'Expense',
}

export const useCreateTransaction = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTransactionPayload) =>
      apiPost<Transaction>('/finance/transactions', {
        ...data,
        type: TYPE_MAP[data.type] ?? data.type,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FINANCE_KEY] }),
  })
}

export const useUpdateTransaction = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTransactionPayload }) =>
      apiPut<Transaction>(`/finance/transactions/${id}`, {
        ...data,
        type: TYPE_MAP[data.type] ?? data.type,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FINANCE_KEY] }),
  })
}

export const useDeleteTransaction = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/finance/transactions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [FINANCE_KEY] }),
  })
}
